import { demoModeAtom, userAtom } from '@/global-store';
import { api, demoUser } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useQuery } from '@tanstack/react-query';
import type { OrganisationUserData } from '@eye-able/types';
import { useAtomValue } from 'jotai';

export interface OrgUser extends OrganisationUserData {}

const demoOrgUser: OrgUser = {
  ...demoUser,
  lastLogin: null,
  licenseConstraintsCount: 0,
};

export interface FetchOrgUsersOptions {
  enabled?: boolean;
  includeAccessSummary?: boolean;
  includeTeams?: boolean;
  reportId?: number | null;
}

export function useFetchOrgUsers(options: FetchOrgUsersOptions = {}) {
  const user = useAtomValue(userAtom);
  const demoMode = useAtomValue(demoModeAtom);
  const includeTeams = options.includeTeams === true;
  const includeAccessSummary = options.includeAccessSummary === true;
  const reportId = options.reportId ?? null;
  const enabled = options.enabled !== false && !!user;

  return useQuery({
    enabled,
    queryKey: [
      api.orgUsers,
      user?.primaryOrganisation,
      demoMode,
      includeTeams,
      includeAccessSummary,
      reportId,
    ],
    queryFn: () => {
      const params: {
        orgId: number | undefined;
        includeTeams?: true;
        includeAccessSummary?: true;
        reportId?: number;
      } = {
        orgId: user?.primaryOrganisation,
      };

      if (includeTeams) {
        params.includeTeams = true;
      }

      if (includeAccessSummary) {
        params.includeAccessSummary = true;
      }

      if (reportId) {
        params.reportId = reportId;
      }

      return demoMode
        ? Promise.resolve([demoOrgUser])
        : fetchWrapper.get<OrgUser[]>(api.orgUsers, params);
    },
  });
}
